'use es6';

import { hasPlugin } from '../../utils';
import { HS_CTA, HS_CTA_INPAGE, HS_ICON, HS_IMAGE, HS_IMAGE_INPAGE, HS_MEDIA, HS_MEDIA_INPAGE, HS_TABLE_V2, HS_VIDEO_V2, HS_VIDEO_V2_INPAGE } from '../../constants/plugins';
import { INLINE_TARGET_ATTR } from '../../constants/inlineTarget';
import { addContextualAlignmentControls, addContextualVerticalAlignmentControls } from './controls/alignment';
import { addContextualMarginControls } from './controls/margin';
import { addContextualDimensionControls } from './controls/dimension';
import { addContextualFillColorControl } from './controls/fillColor';
import { addContextualRotationControl } from './controls/rotation';
import { addContextualIconTypeControl } from './controls/iconType';
import { addContextualBorderColorControl } from './controls/borderColor';
import { addContextualTableStylesControls } from './controls/tableStyles';
const noop = () => {};
const noopNull = () => null;
function addContextualToolbarControls(editor) {
  const hasCtaPlugin = hasPlugin(editor, HS_CTA) || hasPlugin(editor, HS_CTA_INPAGE);
  const hasImagePlugin = hasPlugin(editor, HS_IMAGE) || hasPlugin(editor, HS_IMAGE_INPAGE);
  const hasVideoPlugin = hasPlugin(editor, HS_VIDEO_V2) || hasPlugin(editor, HS_VIDEO_V2_INPAGE);
  const hasEmbedPlugin = hasPlugin(editor, HS_MEDIA) || hasPlugin(editor, HS_MEDIA_INPAGE);
  const hasIconPlugin = hasPlugin(editor, HS_ICON);
  const hasTablePlugin = hasPlugin(editor, HS_TABLE_V2);
  if (hasCtaPlugin || hasImagePlugin || hasVideoPlugin || hasEmbedPlugin || hasTablePlugin) {
    addContextualAlignmentControls(editor);
  }
  if (hasImagePlugin || hasVideoPlugin || hasEmbedPlugin || hasTablePlugin) {
    addContextualMarginControls(editor);
  }
  if (hasImagePlugin || hasVideoPlugin || hasEmbedPlugin || hasIconPlugin || hasTablePlugin) {
    addContextualDimensionControls(editor);
  }
  if (hasVideoPlugin || hasIconPlugin || hasTablePlugin) {
    addContextualFillColorControl(editor);
  }
  if (hasIconPlugin || hasTablePlugin) {
    addContextualVerticalAlignmentControls(editor);
  }
  if (hasIconPlugin) {
    addContextualRotationControl(editor);
    addContextualIconTypeControl(editor);
  }
  if (hasTablePlugin) {
    addContextualBorderColorControl(editor);
    addContextualTableStylesControls(editor);
  }
}
const hasBeenFocused = {};

/**
 *
 * This registers a single handler for all `NodeChange` events serving
 * as the sole logic store for popover manager node changes and
 * contextual toolbar updates.
 */
export default function initContextualToolbarCore(editor) {
  // @TODO: Cache previous contextualToolbar & contextualNodeAttributes to optimize consecutive nodeChanges
  // Table & callout might need special tweaks because they could have popovers inside which
  // would mean multiple `isActiveHandlers` return true --> multiple wanted contextual toolbars

  editor.on('focusin', () => {
    // Mark when the editor has first been focused so we never fire
    // `hsNodeChange` or `ContextualToolbarChange` events prematurely
    hasBeenFocused[editor.id] = true;
  });
  editor.on('remove', () => {
    delete hasBeenFocused[editor.id];
  });
  editor.on('NodeChange', evt => {
    const {
      element
    } = evt;
    let contextualToolbar;
    let contextualToolbarType;
    let activeNodeAttributes = {};
    let formattingAttributes = {};
    if (!editor.nodeChangeHandlers) return;
    editor.nodeChangeHandlers.forEach(config => {
      const {
        eventKey,
        includeInlineTargets = false,
        isActiveNodeHandler = noopNull,
        getActiveNodeAttributes = noop,
        getFormattingAttributes,
        contextualToolbar: _contextualToolbar
      } = config;

      // Always calculate formatting attributes for the toolbar
      if (typeof getFormattingAttributes === 'function') {
        formattingAttributes = Object.assign({}, formattingAttributes, getFormattingAttributes(evt));
      }

      // We don't want inline popovers or contextual toolbars shown
      // when the editor hasn't been focused
      if (!hasBeenFocused[editor.id]) return;

      // The `isActiveNodeHandler` must return null or a valid DOM element
      const activeElement = isActiveNodeHandler({
        element
      });
      const elementIsInlineTarget = element && element.hasAttribute(INLINE_TARGET_ATTR);
      const payload = {
        element: null
      };
      if (activeElement) {
        payload.element = activeElement;
        if (_contextualToolbar) {
          contextualToolbar = _contextualToolbar;
          contextualToolbarType = eventKey;
          activeNodeAttributes = Object.assign({}, getActiveNodeAttributes(activeElement), {
            activeElement
          });
        }
      } else if (includeInlineTargets && elementIsInlineTarget) {
        // We want to fire an `hsNodeChange` for all inline targets
        // as to not prematurely remove a popover manager, but we don't
        // want to trigger a contextual toolbar change
        payload.element = element;
      }

      // Always fire an `hsNodeChange` for popover closing/opening/updating
      // except when `eventKey` is absent (eg. hsformatting)
      if (eventKey) {
        editor.fire(`hsNodeChange:${eventKey}`, payload);
      }
    });
    editor.fire('ContextualToolbarChange', {
      contextualToolbar,
      contextualToolbarType,
      contextualNodeAttributes: Object.assign({}, formattingAttributes, activeNodeAttributes)
    });
  });
  addContextualToolbarControls(editor);
}